import * as Types from '../types';
import { Environment } from '../../../config';
import request from '../../../utils/request';
import { varToStringParams } from 'utils/path';
import { getSessionId } from 'utils/session';

export interface IDataItems {
  id: number;
  guid: string;
  title: string | null;
  createdby: string | number | null;
  status: number;
  createdonutc: string | null;
  lastupdate: string | null;
  storeid: number;
  productid: number;
  private: boolean | null;
  latestmessage: string | null;
  read: boolean | null;
  onlineMembers: number | null;
  virtualpath: string | null;
}

export interface IChatsData {
  items: IDataItems[];
  paging: Types.IPaging;
}

interface IFetchChatsPayload {
  userId?: string;
  productId?: number;
  paging: Partial<Types.IPaging>;
}

export const fetchChats = async ({
  userId,
  paging,
  productId,
}: IFetchChatsPayload): Promise<Types.ResponseOutputV2<IChatsData>> => {
  const params = varToStringParams({
    variablesArray: [
      {
        key: 'pageSize',
        value: paging?.pageSize ?? 1000,
      },
      {
        key: 'userId',
        value: userId,
      },
      {
        key: 'productId',
        value: productId,
      },
    ],
  });

  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/chat/list${params}`
  );

  return data;
};

export interface IChatText {
  id: number;
  fromcustomerid: number;
  text: string;
  status: number;
  visible: string;
  entityname: string;
  entityid: number;
  createdonutc: string | null;
}

export interface IConversationData {
  items: Array<{
    id: string;
    chatid: number;
    chatentityid: number;
    message: string;
    type: number | null;
    status: number | null;
    visible: boolean | null;
    createdonutc: string;
    virtualpath: string | null;
    senderName: string;
    name: string;
    lastname: string;
  }>;
  currentMember: {
    id: number;
    guid: string;
    chatid: number;
    entityid: number;
    entityname: string;
    params: null | string;
    status: number;
    createdat: null | string;
    role: number;
  };
  paging: Types.IPaging;
}

export const fetchChatConversation = async (
  guid: string,
  sessionGuid: string,
  paging: Partial<Types.IPaging>
): Promise<Types.ResponseOutputV2<IConversationData>> => {
  const query = varToStringParams({
    variablesArray: [
      {
        key: 'pageSize',
        value: paging.pageSize ?? 1000,
      },
      {
        key: 'sessionGuid',
        value: sessionGuid,
      },
    ],
  });

  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/chat/${guid}/conversation${query}`
  );
  return data;
};

/** Fetch chat details */
export interface IChatgroupDetails {
  id: number;
  guid: string;
  title: string;
  createdby: string | null;
  status: number;
  type: number | null;
  private: boolean | null;
  createdonutc: string | null;
  lastupdate: string | null;
  storeid: number;
  productid: number;
  chatMembers: Array<{
    id: number;
    guid: string;
    chatid: number;
    entityid: number;
    entityname: string;
    params: null | any;
    status: number;
    createdat: null | string;
    role: number;
  }>;
  owner: boolean;
  read: boolean | null;
  latestmessage: string | null;
  onlineMembers: number;
  virtualpath: string | null;
}

export const fetchChatgroupDetails = async (
  chatgroup: string
): Promise<Types.ResponseOutputV2<IChatgroupDetails>> => {
  const { data }: any = await request(
    'get',
    `${
      Environment.API_BASE_URL
    }/api/chat/${chatgroup}?sessionGuid=${getSessionId()}`
  );
  return data;
};

/** Fetch unread conversations number */
export interface IUnreadConverationsData {
  count: number;
}

export const fetchUnreadConversations = async (): Promise<
  Types.ResponseOutputV2<IUnreadConverationsData>
> => {
  const { data } = await request(
    'get',
    `${
      Environment.API_BASE_URL
    }/api/chat/unread-messages-count?sessionGuid=${getSessionId()}`
  );
  return data;
};
